import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getWhodunit2026Fact } from '../../data/whodunit2026Facts';
import { useWhodunit2026State } from '../../hooks/useWhodunit2026State';
import './EvidenceNotebook.css';

const EvidenceNotebook = () => {
  const { isEnabled, progress } = useWhodunit2026State();
  const [isOpen, setIsOpen] = useState(false);
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const [portalTarget, setPortalTarget] = useState(null);
  const dialogRef = useRef(null);
  const rulesButtonRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    if (isRulesOpen) {
      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
      }
      closeButtonRef.current?.focus();
    } else if (dialog.open) {
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        rulesButtonRef.current?.focus();
      }
    }
  }, [isRulesOpen]);

  if (!isEnabled) {
    return null;
  }

  return (
    <aside className={`evidence-notebook${isOpen ? ' evidence-notebook--open' : ''}`}>
      <button
        type="button"
        className="evidence-notebook__toggle"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-controls="whodunit-evidence"
      >
        Evidence
      </button>
      <div
        id="whodunit-evidence"
        className="evidence-notebook__page"
        aria-hidden={!isOpen}
        inert={isOpen ? undefined : ''}
      >
        <h2 className="evidence-notebook__title">Nine clues</h2>
        <button
          ref={rulesButtonRef}
          type="button"
          className="evidence-notebook__rules-button"
          onClick={() => setIsRulesOpen(true)}
        >
          Read the rules
        </button>
        <ol className="evidence-notebook__slots" aria-label="Collected clues">
          {progress.controllers.map((factId, index) => (
            <li className="evidence-notebook__slot" key={index}>
              <span className="evidence-notebook__number">{index + 1}</span>
              <span className="evidence-notebook__clue">
                {factId ? getWhodunit2026Fact(factId).clue : '—'}
              </span>
            </li>
          ))}
        </ol>
      </div>
      {portalTarget && createPortal(
        <dialog
          ref={dialogRef}
          className="evidence-rules"
          aria-labelledby="evidence-rules-title"
          onCancel={(event) => {
            event.preventDefault();
            setIsRulesOpen(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              setIsRulesOpen(false);
            }
          }}
          onClose={() => {
            setIsRulesOpen(false);
            rulesButtonRef.current?.focus();
          }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setIsRulesOpen(false);
            }
          }}
        >
          <div
            className="evidence-rules__dialog"
          >
            <button
              ref={closeButtonRef}
              type="button"
              className="evidence-rules__close"
              onClick={() => setIsRulesOpen(false)}
              aria-label="Close rules"
            >
              ×
            </button>
            <h2 id="evidence-rules-title" className="evidence-rules__title">Rules of Evidence</h2>
            <ol className="evidence-rules__list">
              <li>Nine facts on this page are incorrect.</li>
              <li>Click a fact when you believe you have found an error.</li>
              <li>The fact will dissolve and reveal a word clue. Its clue is filed in Evidence.</li>
              <li>Solve each word clue and take the requested letter. The nine letters form the birthday password.</li>
              <li>Click revealed secret text again to withdraw it from Evidence.</li>
              <li>Choose carefully: some clickable facts are true, and accusing them may produce misleading evidence.</li>
            </ol>
            <p className="evidence-rules__warning">Trust what you remember. Not every witness is lying.</p>
          </div>
        </dialog>,
        portalTarget,
      )}
    </aside>
  );
};

export default EvidenceNotebook;
